import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { EChartsOption } from 'echarts';
import { CommandeService } from 'src/app/core/services/commande.service';
import { FiltresCommunsRapports } from '../rapports.component';

@Component({
  selector: 'app-rapport-satisfaction',
  templateUrl: './rapport-satisfaction.component.html'
})
export class RapportSatisfactionComponent implements OnInit, OnChanges {
  @Input() filtres!: FiltresCommunsRapports;

  chargement = true;
  erreur: string | null = null;

  private toutesLesEvaluations: any[] = [];

  // KPIs
  noteMoyenne = 0;
  nbAvis = 0;
  nbSatisfaits = 0;
  tauxSatisfactionPct = 0;

  // Filtres retours
  motCle = '';
  noteMax: number | null = null;
  noteMaxSelectionnee: number | null = null;

  // Données retours calculées
  retoursFiltres: any[] = [];

  // Charts
  optDistribution: EChartsOption = {};
  optEvolution: EChartsOption = {};
  optThemes: EChartsOption = {};

  constructor(private commandeService: CommandeService) {}

  ngOnInit(): void { this.charger(); }
  ngOnChanges(): void { this.charger(); }

  charger(): void {
    this.chargement = true;
    this.erreur = null;
    this.commandeService.listerEvaluationsClients(0, 500).subscribe({
      next: (page) => {
        this.toutesLesEvaluations = (page.content || [])
          .filter((c: any) => c.noteSatisfactionClient != null);
        this.calculerStats();
        this.appliquerFiltresRetours();
        this.chargement = false;
      },
      error: () => {
        this.erreur = 'Impossible de charger les données de satisfaction.';
        this.chargement = false;
      }
    });
  }

  private evaluationsDansPeriode(): any[] {
    const debut = this.filtres?.debut ? new Date(this.filtres.debut) : null;
    const fin   = this.filtres?.fin   ? new Date(this.filtres.fin)   : null;
    return this.toutesLesEvaluations.filter((c: any) => {
      if (!c.dateSatisfaction) return true;
      const d = new Date(c.dateSatisfaction);
      if (debut && d < debut) return false;
      if (fin   && d > fin)   return false;
      return true;
    });
  }

  private calculerStats(): void {
    const evs = this.evaluationsDansPeriode();
    this.nbAvis = evs.length;

    if (!this.nbAvis) {
      this.noteMoyenne = 0;
      this.nbSatisfaits = 0;
      this.tauxSatisfactionPct = 0;
      this.optDistribution = {};
      this.optEvolution = {};
      return;
    }

    const somme = evs.reduce((acc: number, c: any) => acc + Number(c.noteSatisfactionClient ?? 0), 0);
    this.noteMoyenne = Math.round(somme / this.nbAvis * 10) / 10;
    this.nbSatisfaits = evs.filter((c: any) => Number(c.noteSatisfactionClient ?? 0) >= 4).length;
    this.tauxSatisfactionPct = Math.round(this.nbSatisfaits / this.nbAvis * 1000) / 10;

    const distrib: { [k: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    evs.forEach((c: any) => {
      let n = Math.round(Number(c.noteSatisfactionClient ?? 0));
      n = Math.max(1, Math.min(5, n));
      distrib[n] = (distrib[n] || 0) + 1;
    });

    const evMap: { [k: string]: number[] } = {};
    evs.forEach((c: any) => {
      if (!c.dateSatisfaction) return;
      const mois = String(c.dateSatisfaction).substring(0, 7);
      if (!evMap[mois]) evMap[mois] = [];
      evMap[mois].push(Number(c.noteSatisfactionClient ?? 0));
    });
    const evolution: { [k: string]: number } = {};
    Object.keys(evMap).sort().forEach(mois => {
      const notes = evMap[mois];
      evolution[mois] = Math.round(notes.reduce((a, b) => a + b, 0) / notes.length * 10) / 10;
    });

    this.optDistribution = {
      tooltip: { trigger: 'axis' },
      grid: { left: 40, right: 20, top: 20, bottom: 30 },
      xAxis: { type: 'category', data: ['1★', '2★', '3★', '4★', '5★'], axisLabel: { color: '#64748b' } },
      yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { color: '#64748b' } },
      series: [{
        type: 'bar',
        data: [distrib[1], distrib[2], distrib[3], distrib[4], distrib[5]],
        itemStyle: {
          color: (p: any) => ['#dc2626', '#f97316', '#f59e0b', '#10b981', '#059669'][p.dataIndex],
          borderRadius: [4, 4, 0, 0]
        },
        barWidth: 38
      }]
    };

    const keys = Object.keys(evolution);
    this.optEvolution = {
      tooltip: { trigger: 'axis' },
      grid: { left: 40, right: 20, top: 30, bottom: 30 },
      xAxis: { type: 'category', data: keys, axisLabel: { color: '#64748b', fontSize: 11 } },
      yAxis: { type: 'value', min: 0, max: 5, splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { color: '#64748b' } },
      series: [{
        name: 'Note moyenne', type: 'line', smooth: true,
        data: keys.map(k => evolution[k]),
        lineStyle: { color: '#10b981', width: 2.5 },
        itemStyle: { color: '#10b981' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(16,185,129,0.3)' },
              { offset: 1, color: 'rgba(16,185,129,0)' }
            ]
          }
        }
      }]
    };
  }

  appliquerFiltresRetours(): void {
    this.noteMax = this.noteMaxSelectionnee;

    const evs = this.evaluationsDansPeriode();
    let filtres = evs.filter((c: any) => (c.commentaireSatisfaction ?? '').trim().length > 0);

    if (this.noteMax != null) {
      filtres = filtres.filter((c: any) => Number(c.noteSatisfactionClient ?? 0) <= this.noteMax!);
    }
    if (this.motCle.trim()) {
      const kw = this.motCle.trim().toLowerCase();
      filtres = filtres.filter((c: any) =>
        (c.commentaireSatisfaction ?? '').toLowerCase().includes(kw) ||
        (c.clientNom ?? '').toLowerCase().includes(kw)
      );
    }

    filtres.sort((a: any, b: any) => {
      if (!a.dateSatisfaction) return 1;
      if (!b.dateSatisfaction) return -1;
      return new Date(b.dateSatisfaction).getTime() - new Date(a.dateSatisfaction).getTime();
    });

    this.retoursFiltres = filtres.slice(0, 50);

    const th: { [k: string]: number } = { DELAI: 0, PERTE: 0, QUALITE: 0, LIVREUR: 0, PRIX: 0, AUTRE: 0 };
    filtres.forEach((c: any) => {
      const t = (c.commentaireSatisfaction ?? '').toLowerCase();
      let classe = false;
      if (/retard|délai|temps|lent/.test(t))                        { th['DELAI']++;   classe = true; }
      if (/perte|perdu|vol/.test(t))                                 { th['PERTE']++;   classe = true; }
      if (/abîmé|abime|cassé|endomma|qualité/.test(t))              { th['QUALITE']++; classe = true; }
      if (/livreur|chauffeur|accueil/.test(t))                       { th['LIVREUR']++; classe = true; }
      if (/prix|cher|tarif/.test(t))                                 { th['PRIX']++;    classe = true; }
      if (!classe) th['AUTRE']++;
    });

    const labelsThemes: { [k: string]: string } = {
      DELAI: 'Délai', PERTE: 'Perte / Vol', QUALITE: 'Qualité produit',
      LIVREUR: 'Livreur', PRIX: 'Prix / Tarif', AUTRE: 'Autre'
    };
    const pieData = Object.entries(th).filter(([, v]) => v > 0).map(([k, v]) => ({ name: labelsThemes[k] || k, value: v }));

    this.optThemes = pieData.length ? {
      tooltip: { trigger: 'item' },
      legend: { bottom: 0, type: 'scroll' },
      series: [{
        type: 'pie', radius: ['45%', '70%'], avoidLabelOverlap: true,
        itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
        label: { show: false }, labelLine: { show: false },
        data: pieData
      }]
    } : {};
  }

  recherchePersonnalisee(): void {
    this.appliquerFiltresRetours();
  }

  selectionnerEtoile(n: number): void {
    this.noteMaxSelectionnee = this.noteMaxSelectionnee === n ? null : n;
  }

  etoileActive(n: number): boolean {
    return this.noteMaxSelectionnee != null && n <= this.noteMaxSelectionnee;
  }

  etoiles(note: any): string {
    const n = Math.max(0, Math.min(5, Math.round(Number(note ?? 0))));
    return '★'.repeat(n) + '☆'.repeat(5 - n);
  }

  classeNote(note: any): string {
    const n = Number(note ?? 0);
    if (n >= 4) return 'note-excellente';
    if (n >= 3) return 'note-correcte';
    return 'note-faible';
  }
}
